import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Workflow, Plus, Trash2, Edit3, Loader2, Save, Zap, Mail, CheckSquare, Bell, History, X, ClipboardList, MessageSquare, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGERS = [
  { value: "card_created", label: "Cuando se crea una oportunidad (lead nuevo)" },
  { value: "stage_enter", label: "Cuando entra a una etapa" },
  { value: "won", label: "Cuando se marca Ganada" },
  { value: "lost", label: "Cuando se marca Perdida" },
  { value: "days_in_stage", label: "Cuando lleva X días en una etapa sin avanzar" },
  { value: "task_created", label: "Tarea — cuando se crea una tarea" },
  { value: "task_status_changed", label: "Tarea — cuando cambia a un estatus" },
  { value: "task_completed", label: "Tarea — cuando se completa" },
];
const TASK_STATUS = [
  { value: "", label: "Cualquier estatus" },
  { value: "no_iniciada", label: "No iniciada" },
  { value: "en_progreso", label: "En progreso" },
  { value: "esperando", label: "Esperando" },
  { value: "aplazada", label: "Aplazada" },
  { value: "completada", label: "Completada" },
];
const OPS = [
  { value: "eq", label: "es igual a" }, { value: "ne", label: "es distinto de" },
  { value: "contains", label: "contiene" }, { value: "not_contains", label: "no contiene" },
  { value: "gt", label: "mayor que" }, { value: "lt", label: "menor que" },
  { value: "empty", label: "está vacío" }, { value: "not_empty", label: "no está vacío" },
];
const ACTION_TYPES = [
  { value: "send_email", label: "Enviar correo", icon: Mail },
  { value: "create_task", label: "Crear tarea", icon: CheckSquare },
  { value: "notify", label: "Notificar (interno)", icon: Bell },
  { value: "require_field", label: "Exigir completar campo(s) al entrar a la etapa", icon: ClipboardList },
  { value: "require_comment", label: "Exigir comentario/justificación al entrar a la etapa", icon: MessageSquare },
];
const RECIPIENTS = [
  { value: "cliente", label: "Cliente" }, { value: "contacto", label: "Contacto" },
  { value: "propietario", label: "Propietario del negocio" },
  { value: "supervisor", label: "Supervisor del propietario" },
  { value: "asignado", label: "Asignado de la tarea" },
  { value: "admin", label: "Administradores" },
  { value: "vendedor", label: "Vendedor asignado" }, { value: "fijo", label: "Email(s) fijos" },
];
const emptyRule = (pid) => ({ pipeline_id: pid, name: "", activo: true, trigger: { type: "card_created" }, conditions: [], actions: [] });

export default function WorkflowPage() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());
  const [pipelines, setPipelines] = useState([]);
  const [activePid, setActivePid] = useState(null);
  const [rules, setRules] = useState([]);
  const [stages, setStages] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  // Datos específicos del pipeline elegido DENTRO del diálogo (dinámicos)
  const [editStages, setEditStages] = useState([]);
  const [editFields, setEditFields] = useState([]);
  const [editPlantillas, setEditPlantillas] = useState([]);
  const [editVariables, setEditVariables] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API_URL}/api/pipelines`, getAuthHeaders());
      const pipes = (await r.json()).items || [];
      setPipelines(pipes);
      if (pipes[0]) setActivePid(pipes[0].id);
      setLoading(false);
    })();
  }, [getAuthHeaders]);

  const loadPipelineData = useCallback(async (pid) => {
    if (!pid) return;
    const [rr, rs, rp, rv] = await Promise.all([
      fetch(`${API_URL}/api/pipelines/workflows?pipeline_id=${pid}`, getAuthHeaders()),
      fetch(`${API_URL}/api/pipelines/${pid}`, getAuthHeaders()),
      fetch(`${API_URL}/api/pipelines/plantillas?pipeline_id=${pid}`, getAuthHeaders()),
      fetch(`${API_URL}/api/pipelines/variables/${pid}`, getAuthHeaders()),
    ]);
    setRules((await rr.json()).items || []);
    setStages(((await rs.json()).stages || []).sort((a, b) => a.order - b.order));
    setPlantillas((await rp.json()).items || []);
    setVariables((await rv.json()).items || []);
  }, [getAuthHeaders]);

  useEffect(() => { if (activePid) loadPipelineData(activePid); }, [activePid, loadPipelineData]);

  // Carga dinámica de etapas/plantillas/variables según el pipeline elegido DENTRO del diálogo
  useEffect(() => {
    const pid = editing?.pipeline_id;
    if (!pid) { setEditStages([]); setEditFields([]); setEditPlantillas([]); setEditVariables([]); return; }
    let cancelled = false;
    (async () => {
      setEditLoading(true);
      try {
        const [rs, rp, rv] = await Promise.all([
          fetch(`${API_URL}/api/pipelines/${pid}`, getAuthHeaders()),
          fetch(`${API_URL}/api/pipelines/plantillas?pipeline_id=${pid}`, getAuthHeaders()),
          fetch(`${API_URL}/api/pipelines/variables/${pid}`, getAuthHeaders()),
        ]);
        if (cancelled) return;
        const pj = await rs.json();
        setEditStages((pj.stages || []).sort((a, b) => a.order - b.order));
        setEditFields((pj.fields || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
        setEditPlantillas((await rp.json()).items || []);
        setEditVariables((await rv.json()).items || []);
      } finally { if (!cancelled) setEditLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [editing?.pipeline_id, getAuthHeaders]);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("La regla necesita un nombre"); return; }
    if (!editing.pipeline_id) { toast.error("Elegí a qué pipeline aplica la regla"); return; }
    setSaving(true);
    try {
      const isEdit = !!editing.id;
      const r = await fetch(`${API_URL}/api/pipelines/workflows${isEdit ? "/" + editing.id : ""}`, {
        method: isEdit ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ...editing, pipeline_id: editing.pipeline_id }),
      });
      if (!r.ok) throw new Error();
      const targetPid = editing.pipeline_id;
      toast.success("Regla guardada"); setEditing(null);
      setActivePid(targetPid); loadPipelineData(targetPid);
    } catch { toast.error("Error al guardar la regla"); }
    finally { setSaving(false); }
  };

  const toggleActivo = async (rule) => {
    try {
      await fetch(`${API_URL}/api/pipelines/workflows/${rule.id}`, {
        method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ...rule, activo: !rule.activo }),
      });
      loadPipelineData(activePid);
    } catch { toast.error("Error al actualizar"); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`¿Eliminar la regla "${rule.name}"?`)) return;
    try {
      await fetch(`${API_URL}/api/pipelines/workflows/${rule.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      toast.success("Regla eliminada"); loadPipelineData(activePid);
    } catch { toast.error("Error al eliminar"); }
  };

  const openLogs = async () => {
    const r = await fetch(`${API_URL}/api/pipelines/workflow-logs?pipeline_id=${activePid}`, getAuthHeaders());
    setLogs((await r.json()).items || []); setShowLogs(true);
  };

  const triggerLabel = (t) => TRIGGERS.find((x) => x.value === t?.type)?.label || t?.type;
  const stageName = (id) => stages.find((s) => s.id === id)?.name || "";

  // ---- editing helpers ----
  const setTrigger = (patch) => setEditing({ ...editing, trigger: { ...editing.trigger, ...patch } });
  const addCondition = () => setEditing({ ...editing, conditions: [...editing.conditions, { field: "ramo", op: "eq", value: "" }] });
  const updCondition = (i, patch) => setEditing({ ...editing, conditions: editing.conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const delCondition = (i) => setEditing({ ...editing, conditions: editing.conditions.filter((_, idx) => idx !== i) });
  const addAction = () => setEditing({ ...editing, actions: [...editing.actions, { type: "send_email", recipients: ["cliente"], fixed_emails: "" }] });
  const updAction = (i, patch) => setEditing({ ...editing, actions: editing.actions.map((a, idx) => idx === i ? { ...a, ...patch } : a) });
  const delAction = (i) => setEditing({ ...editing, actions: editing.actions.filter((_, idx) => idx !== i) });

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-64 overflow-x-hidden">
        <div className="p-4 pt-14 md:pt-4 max-w-[1400px] mx-auto" data-testid="workflow-page">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#193966] to-[#80BC00] text-white flex items-center justify-center">
                <Workflow className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#193966]">Workflow</h1>
                <p className="text-xs text-slate-500">Reglas automáticas: qué hacer y a quién notificar en cada etapa</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select value={activePid || ""} onChange={(e) => setActivePid(e.target.value)} className="h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="workflow-pipeline-select">
                {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={openLogs} className="h-9" data-testid="workflow-logs-btn">
                <History className="w-4 h-4 mr-1.5" /> Historial
              </Button>
              {isAdmin && (
                <Button size="sm" onClick={() => setEditing(emptyRule(activePid))} className="h-9 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="workflow-new-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Nueva regla
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center p-12"><Loader2 className="w-7 h-7 mx-auto animate-spin text-slate-400" /></div>
          ) : rules.length === 0 ? (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center bg-white">
              <Zap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-700 mb-2">Sin reglas en este pipeline</h2>
              <p className="text-sm text-slate-500">Creá tu primera automatización: ej. "cuando entra un lead → enviar correo de bienvenida".</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div key={rule.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={`workflow-card-${rule.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800">{rule.name}</h3>
                        <Badge className={rule.activo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>{rule.activo ? "Activa" : "Inactiva"}</Badge>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        <Zap className="w-3 h-3 inline mr-1 text-amber-500" />
                        {triggerLabel(rule.trigger)}
                        {rule.trigger?.stage_id && <span className="text-slate-400"> · {stageName(rule.trigger.stage_id)}</span>}
                        {rule.trigger?.type === "days_in_stage" && <span className="text-slate-400"> · {rule.trigger?.days} días</span>}
                        {rule.trigger?.type === "task_status_changed" && rule.trigger?.task_status && <span className="text-slate-400"> · estatus: {rule.trigger.task_status}</span>}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(rule.actions || []).map((a, i) => {
                          const A = ACTION_TYPES.find((x) => x.value === a.type);
                          const Icon = A?.icon || Zap;
                          return <Badge key={i} variant="outline" className="text-[10px]"><Icon className="w-3 h-3 mr-1" />{A?.label || a.type}</Badge>;
                        })}
                        {(rule.conditions || []).length > 0 && <Badge variant="outline" className="text-[10px] text-indigo-600">{rule.conditions.length} condición(es)</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isAdmin && <Switch checked={rule.activo} onCheckedChange={() => toggleActivo(rule)} data-testid={`workflow-toggle-${rule.id}`} />}
                      {isAdmin && <Button size="sm" variant="outline" className="h-8" title="Clonar regla" onClick={() => { const { id, created_at, updated_at, ...rest } = rule; setEditing({ ...emptyRule(activePid), ...rest, name: `${rule.name} (copia)` }); }} data-testid={`workflow-clone-${rule.id}`}><Copy className="w-3.5 h-3.5" /></Button>}
                      {isAdmin && <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing({ ...emptyRule(activePid), ...rule })} data-testid={`workflow-edit-${rule.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>}
                      {isAdmin && <Button size="sm" variant="outline" className="h-8 text-rose-600" onClick={() => remove(rule)} data-testid={`workflow-delete-${rule.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rule editor */}
      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="workflow-dialog">
            <DialogHeader><DialogTitle>{editing.id ? "Editar regla" : "Nueva regla de workflow"}</DialogTitle></DialogHeader>
            <div className="space-y-5">
              <div>
                <Label className="text-xs">Nombre de la regla *</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ej: Bienvenida al nuevo lead" data-testid="workflow-name" />
              </div>

              {/* TRIGGER */}
              <div className="border rounded-lg p-3 bg-amber-50/50">
                <Label className="text-xs font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> Disparador (cuándo)</Label>
                <select value={editing.trigger.type} onChange={(e) => setTrigger({ type: e.target.value })} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white mt-2" data-testid="workflow-trigger-type">
                  {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>

                {/* PIPELINE: se elige luego del disparador y antes de la etapa */}
                <div className="mt-3">
                  <Label className="text-[10px] font-semibold">Pipeline al que aplica *</Label>
                  <select
                    value={editing.pipeline_id || ""}
                    onChange={(e) => setEditing({ ...editing, pipeline_id: e.target.value, trigger: { ...editing.trigger, stage_id: "" }, actions: editing.actions.map((a) => a.type === "send_email" ? { ...a, template_id: "" } : a) })}
                    className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white mt-1"
                    data-testid="workflow-trigger-pipeline">
                    <option value="">Seleccioná un pipeline…</option>
                    {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                {(editing.trigger.type === "stage_enter" || editing.trigger.type === "days_in_stage") && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label className="text-[10px]">Etapa</Label>
                      <select value={editing.trigger.stage_id || ""} onChange={(e) => setTrigger({ stage_id: e.target.value })} disabled={!editing.pipeline_id || editLoading} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400" data-testid="workflow-trigger-stage">
                        <option value="">{!editing.pipeline_id ? "Elegí un pipeline primero…" : editLoading ? "Cargando etapas…" : (editing.trigger.type === "stage_enter" ? "Cualquier etapa" : "Seleccioná etapa…")}</option>
                        {editStages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    {editing.trigger.type === "days_in_stage" && (
                      <div>
                        <Label className="text-[10px]">Días sin avanzar</Label>
                        <Input type="number" min={1} value={editing.trigger.days || ""} onChange={(e) => setTrigger({ days: Number(e.target.value) || 0 })} data-testid="workflow-trigger-days" />
                      </div>
                    )}
                  </div>
                )}

                {editing.trigger.type === "task_status_changed" && (
                  <div className="mt-2">
                    <Label className="text-[10px]">Disparar cuando el estatus de la tarea sea</Label>
                    <select
                      value={editing.trigger.task_status || ""}
                      onChange={(e) => setTrigger({ task_status: e.target.value })}
                      className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white mt-1"
                      data-testid="workflow-trigger-task-status"
                    >
                      {TASK_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <p className="text-[10px] text-slate-400 mt-1">Ej: al pasar a "Completada" o "En progreso", avisar a propietario/supervisor.</p>
                  </div>
                )}
              </div>

              {/* CONDITIONS */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold uppercase tracking-wide text-indigo-700">Condiciones (opcional)</Label>
                  <Button size="sm" variant="outline" className="h-7" onClick={addCondition} data-testid="workflow-add-condition"><Plus className="w-3 h-3 mr-1" /> Condición</Button>
                </div>
                {editing.conditions.length === 0 && <p className="text-[11px] text-slate-400 mt-1">Sin condiciones: la regla se aplica siempre que ocurra el disparador.</p>}
                {editing.conditions.map((c, i) => (
                  <div key={i} className="flex items-center gap-1.5 mt-2">
                    <select value={c.field} onChange={(e) => updCondition(i, { field: e.target.value })} className="h-8 border border-slate-300 rounded px-1 text-xs bg-white flex-1">
                      {editVariables.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                    </select>
                    <select value={c.op} onChange={(e) => updCondition(i, { op: e.target.value })} className="h-8 border border-slate-300 rounded px-1 text-xs bg-white">
                      {OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!["empty", "not_empty"].includes(c.op) && <Input value={c.value} onChange={(e) => updCondition(i, { value: e.target.value })} className="h-8 text-xs flex-1" placeholder="valor" />}
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-500" onClick={() => delCondition(i)}><X className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>

              {/* ACTIONS */}
              <div className="border rounded-lg p-3 bg-green-50/40">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold uppercase tracking-wide text-green-700">Acciones (qué hacer)</Label>
                  <Button size="sm" variant="outline" className="h-7" onClick={addAction} data-testid="workflow-add-action"><Plus className="w-3 h-3 mr-1" /> Acción</Button>
                </div>
                {editing.actions.length === 0 && <p className="text-[11px] text-rose-400 mt-1">Agregá al menos una acción.</p>}
                {editing.actions.map((a, i) => (
                  <div key={i} className="border rounded p-2 mt-2 bg-white" data-testid={`workflow-action-${i}`}>
                    <div className="flex items-center gap-2">
                      <select value={a.type} onChange={(e) => updAction(i, { type: e.target.value })} className="h-8 border border-slate-300 rounded px-2 text-xs bg-white flex-1">
                        {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-500" onClick={() => delAction(i)}><X className="w-4 h-4" /></Button>
                    </div>

                    {a.type === "send_email" && (
                      <div className="mt-2 space-y-2">
                        <div>
                          <Label className="text-[10px]">Plantilla</Label>
                          <select value={a.template_id || ""} onChange={(e) => updAction(i, { template_id: e.target.value })} className="w-full h-8 border border-slate-300 rounded px-2 text-xs bg-white" data-testid={`workflow-action-template-${i}`}>
                            <option value="">Seleccioná una plantilla…</option>
                            {editPlantillas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          {editPlantillas.length === 0 && <p className="text-[10px] text-rose-400 mt-1">No hay plantillas. Creá una en el submenú Plantillas.</p>}
                        </div>
                        <div>
                          <Label className="text-[10px]">Destinatarios</Label>
                          <div className="flex flex-wrap gap-3 mt-1">
                            {RECIPIENTS.map((r) => (
                              <label key={r.value} className="flex items-center gap-1 text-xs">
                                <Checkbox checked={(a.recipients || []).includes(r.value)} onCheckedChange={(v) => {
                                  const set = new Set(a.recipients || []);
                                  if (v) set.add(r.value); else set.delete(r.value);
                                  updAction(i, { recipients: [...set] });
                                }} />
                                {r.label}
                              </label>
                            ))}
                          </div>
                          {(a.recipients || []).includes("fijo") && (
                            <Input value={a.fixed_emails || ""} onChange={(e) => updAction(i, { fixed_emails: e.target.value })} className="h-8 text-xs mt-1" placeholder="gerencia@empresa.com, otro@empresa.com" />
                          )}
                        </div>
                      </div>
                    )}

                    {a.type === "create_task" && (
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                          <Label className="text-[10px]">Título de la tarea (admite variables)</Label>
                          <Input value={a.task_title || ""} onChange={(e) => updAction(i, { task_title: e.target.value })} className="h-8 text-xs" placeholder="Llamar a {{cliente}}" />
                        </div>
                        <div>
                          <Label className="text-[10px]">Vence en (días)</Label>
                          <Input type="number" min={0} value={a.task_days_offset ?? 1} onChange={(e) => updAction(i, { task_days_offset: Number(e.target.value) || 0 })} className="h-8 text-xs" />
                        </div>
                      </div>
                    )}

                    {a.type === "notify" && (
                      <div className="mt-2 space-y-2">
                        <div>
                          <Label className="text-[10px]">Destinatarios</Label>
                          <div className="flex flex-wrap gap-3 mt-1">
                            {RECIPIENTS.map((r) => (
                              <label key={r.value} className="flex items-center gap-1 text-xs">
                                <Checkbox checked={(a.recipients || []).includes(r.value)} onCheckedChange={(v) => {
                                  const set = new Set(a.recipients || []);
                                  if (v) set.add(r.value); else set.delete(r.value);
                                  updAction(i, { recipients: [...set] });
                                }} />
                                {r.label}
                              </label>
                            ))}
                          </div>
                          {(a.recipients || []).includes("fijo") && (
                            <Input value={a.fixed_emails || ""} onChange={(e) => updAction(i, { fixed_emails: e.target.value })} className="h-8 text-xs mt-1" placeholder="gerencia@empresa.com, otro@empresa.com" />
                          )}
                          <p className="text-[10px] text-slate-400 mt-1">Ideal para tareas: avisar al propietario, supervisor o asignado.</p>
                        </div>
                        <div>
                          <Label className="text-[10px]">Mensaje (admite variables)</Label>
                          <Textarea value={a.message || ""} onChange={(e) => updAction(i, { message: e.target.value })} rows={2} className="text-xs" placeholder="Recordá dar seguimiento a {{cliente}}" />
                        </div>
                      </div>
                    )}

                    {(a.type === "require_field" || a.type === "require_comment") && editing.trigger.type !== "stage_enter" && (
                      <p className="text-[10px] text-amber-600 mt-1.5">Esta acción solo funciona con el disparador "Cuando entra a una etapa".</p>
                    )}

                    {a.type === "require_field" && (
                      <div className="mt-2">
                        <Label className="text-[10px]">Campo(s) del pipeline a exigir (bloquea el movimiento hasta completarlos)</Label>
                        {editFields.length === 0 ? (
                          <p className="text-[10px] text-rose-400 mt-1">Este pipeline no tiene campos personalizados.</p>
                        ) : (
                          <div className="flex flex-wrap gap-3 mt-1">
                            {editFields.map((f) => (
                              <label key={f.id} className="flex items-center gap-1 text-xs">
                                <Checkbox
                                  checked={(a.field_names || []).includes(f.name)}
                                  onCheckedChange={(v) => {
                                    const set = new Set(a.field_names || []);
                                    if (v) set.add(f.name); else set.delete(f.name);
                                    updAction(i, { field_names: [...set] });
                                  }}
                                  data-testid={`workflow-reqfield-${i}-${f.name}`}
                                />
                                {f.label}
                              </label>
                            ))}
                          </div>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">Combinalo con las condiciones de arriba (ej: "Fecha de Visita está vacía" → exigir Fecha Reagendada).</p>
                      </div>
                    )}

                    {a.type === "require_comment" && (
                      <div className="mt-2">
                        <Label className="text-[10px]">Texto de instrucción que verá el usuario</Label>
                        <Input
                          value={a.comment_prompt || ""}
                          onChange={(e) => updAction(i, { comment_prompt: e.target.value })}
                          className="h-8 text-xs"
                          placeholder='Ej: "Escriba los comentarios de la visita realizada"'
                          data-testid={`workflow-comment-prompt-${i}`}
                        />
                        <p className="text-[10px] text-slate-400 mt-1">El comentario es obligatorio para entrar a la etapa y se guarda como nota de la tarjeta.</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <label className="flex items-center gap-2 text-sm">
                <Switch checked={editing.activo} onCheckedChange={(v) => setEditing({ ...editing, activo: v })} data-testid="workflow-active" />
                Regla activa
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="workflow-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar regla
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Logs / traceability */}
      {showLogs && (
        <Dialog open onOpenChange={() => setShowLogs(false)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="workflow-logs-dialog">
            <DialogHeader><DialogTitle>Historial de automatizaciones (trazabilidad)</DialogTitle></DialogHeader>
            {logs.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Aún no hay ejecuciones registradas.</p>
            ) : (
              <div className="space-y-1.5">
                {logs.map((l) => (
                  <div key={l.id} className="flex items-center gap-2 text-xs border-b pb-1.5">
                    <Badge className={l.status === "ok" ? "bg-green-100 text-green-700" : l.status === "skipped" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}>{l.status}</Badge>
                    <span className="font-semibold text-slate-700">{l.rule_name}</span>
                    <span className="text-slate-500">· {l.action_type} · {l.card_title}</span>
                    <span className="text-slate-400 ml-auto">{(l.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter><Button variant="outline" onClick={() => setShowLogs(false)}>Cerrar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
